import React from 'react';
import { Card } from '../../../../components/display/Card';
import { Icon } from '../../../../components/base/Icon';
import { Select } from '../../../../components/forms/Select';
import { FieldConfiguration, ListBuilderConfiguration } from '../../../types';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { Text } from '../../../../components/base/Text';
import { Input } from '../../../../components/forms/Input';
import { Button } from '../../../../components/forms/Button';

interface LinkConfiguratorProps {
  field: FieldConfiguration;
  configuration?: ListBuilderConfiguration;
  onUpdateField: (updates: Partial<FieldConfiguration>) => void;
  isModalOpen?: boolean;
}

export function LinkConfigurator({
  field,
  configuration,
  onUpdateField,
  isModalOpen = false
}: LinkConfiguratorProps) {
  const colors = useThemeColors();
  // Hide link configuration for display styles that have implicit links
  if (field.displayStyle === 'dot' || field.displayStyle === 'email' || field.displayStyle === 'phone') {
    return null;
  }

  return (
    <Card className="mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <Icon icon={faLink} size="sm" color={colors.values.textPrimary} className="mr-2" />
            <Text className="text-base font-semibold">
              Link Settings
            </Text>
          </div>
          <Button
            onClick={() => onUpdateField({ 
              link: { 
                ...field.link, 
                enabled: !field.link?.enabled,
                type: field.link?.type || 'self'
              } 
            })}
            size="sm"
            variant={field.link?.enabled ? "primary" : "secondary"}
          >
            {field.link?.enabled ? 'Enabled' : 'Disabled'}
          </Button>
        </div>

        {field.link?.enabled && (
          <>
            <div className="mb-3">
              <Text className="text-sm font-medium mb-2">
                Link Type
              </Text>
              <Select
                value={field.link.type}
                options={[
                  { value: 'self', label: 'Self' },
                  { value: 'object', label: 'Object' },
                  { value: 'custom', label: 'Custom' },
                  { value: 'callback', label: 'Callback Function' }
                ]}
                onChange={(value) => {
                  onUpdateField({ 
                    link: { 
                      ...field.link, 
                      enabled: true, 
                      type: value as any
                    } 
                  });
                }}
              />
            </div>

            {field.link.type === 'object' && (
              <div className="mt-3">
                <Text className="text-sm font-medium mb-2">
                  Target Object
                </Text>
                <Select
                  value={field.link.objectTable}
                  options={(() => {
                    // If no objects in configuration, provide default options
                    const configObjects = configuration?.objects?.map(obj => ({
                      value: obj.objectTable,
                      label: obj.objectLabel
                    })) || [];
                    
                    // Add common object types if none are configured
                    if (configObjects.length === 0) {
                      return [
                        { value: 'item', label: 'Item' },
                        { value: 'client', label: 'Client' },
                        { value: 'order', label: 'Order' },
                        { value: 'invoice', label: 'Invoice' },
                        { value: 'vendor', label: 'Vendor' },
                        { value: 'warehouse', label: 'Warehouse' }
                      ];
                    }
                    
                    return configObjects;
                  })()}
                  onChange={(value) => {
                    onUpdateField({ 
                      link: { 
                        ...field.link, 
                        enabled: true, 
                        type: 'object',
                        objectTable: value
                      } 
                    });
                  }}
                  placeholder="Select object..."
                />
              </div>
            )}

            {field.link.type === 'custom' && (
              <div className="mt-3">
                <Text className="text-sm font-medium mb-2">
                  Custom URL Template
                </Text>
                <Input
                  type="text"
                  value={field.link.customUrl || ''}
                  onChange={(e) => onUpdateField({ 
                    link: { 
                      ...field.link, 
                      enabled: true, 
                      type: 'custom', 
                      customUrl: e.target.value 
                    } 
                  })}
                  placeholder="https://example.com/{value}"
                  size="sm"
                />
                <Text className="text-xs mt-1" color="secondary">
                  Use {'{value}'} to insert the field value
                </Text>
              </div>
            )}

            {field.link.type === 'callback' && (
              <div className="mt-3">
                <Text className="text-sm font-medium mb-2">
                  Callback Name
                </Text>
                <Input
                  type="text"
                  value={field.link.callbackName || ''}
                  onChange={(e) => onUpdateField({ 
                    link: { 
                      ...field.link, 
                      enabled: true, 
                      type: 'callback', 
                      callbackName: e.target.value 
                    } 
                  })}
                  placeholder="e.g., openEditModal"
                  size="sm"
                />
                <Text className="text-xs mt-1" color="secondary">
                  Name of the callback function to invoke
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}